import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { useUser } from "@/contexts/UserContext";
import MainLayout from "@/components/MainLayout";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Shield, Eye, Pencil, UserCheck, UserX, UsersRound } from "lucide-react";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { user: currentUser } = useUser();

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("You don't have permission to view users");
      } else {
        toast.error("Failed to load users");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleToggleStatus = async (user) => {
    const newStatus = user.status === "active" ? "suspended" : "active";
    try {
      await axios.put(`${API}/users/${user.id}/status`, { status: newStatus });
      toast.success(`User ${newStatus === "active" ? "activated" : "suspended"}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user status");
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/users/${deleteTarget.id}`);
      toast.success("User deleted successfully");
      setDeleteTarget(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      owner: { icon: Shield, color: "bg-purple-500/20 text-purple-400", label: "Owner" },
      editor: { icon: Pencil, color: "bg-blue-500/20 text-blue-400", label: "Editor" },
      viewer: { icon: Eye, color: "bg-gray-500/20 text-gray-400", label: "Viewer" },
    };
    const badge = badges[role] || badges.viewer;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const isActive = status !== "suspended";
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
          isActive ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"
        }`}
        data-testid={`status-badge-${isActive ? "active" : "suspended"}`}
      >
        {isActive ? <UserCheck className="w-3 h-3" /> : <UserX className="w-3 h-3" />}
        {isActive ? "Active" : "Suspended"}
      </span>
    );
  };

  return (
    <MainLayout title="Users" subtitle="Manage all registered users in the system">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-3">
            <span className="text-sm text-gray-400">Total users: </span>
            <span className="text-sm font-medium text-white" data-testid="total-users-count">{users.length}</span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#10B981]"></div>
          </div>
        ) : users.length === 0 ? (
          <div className="bg-white/5 border border-white/10 rounded-xl p-12 text-center">
            <UsersRound className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No users found</h3>
            <p className="text-gray-400">There are no registered users in the system.</p>
          </div>
        ) : (
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-gray-400">Name</TableHead>
                  <TableHead className="text-gray-400">Email</TableHead>
                  <TableHead className="text-gray-400">Role</TableHead>
                  <TableHead className="text-gray-400">Status</TableHead>
                  <TableHead className="text-gray-400">Created</TableHead>
                  <TableHead className="text-gray-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} className="border-white/10 hover:bg-white/5" data-testid={`user-row-${user.id}`}>
                    <TableCell className="text-white font-medium">{user.name || "-"}</TableCell>
                    <TableCell className="text-gray-300">{user.email}</TableCell>
                    <TableCell>{getRoleBadge(user.role)}</TableCell>
                    <TableCell>{getStatusBadge(user.status)}</TableCell>
                    <TableCell className="text-gray-400">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      {currentUser?.id !== user.id && (
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleStatus(user)}
                            className={`text-xs ${
                              user.status === "suspended"
                                ? "text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                                : "text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                            }`}
                            data-testid={`toggle-status-${user.id}`}
                          >
                            {user.status === "suspended" ? (
                              <>
                                <UserCheck className="w-4 h-4 mr-1" />
                                Activate
                              </>
                            ) : (
                              <>
                                <UserX className="w-4 h-4 mr-1" />
                                Suspend
                              </>
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteTarget(user)}
                            className="text-gray-400 hover:text-red-400 hover:bg-red-500/10"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
          <DialogContent className="bg-[#1a1a24] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle>Delete User</DialogTitle>
              <DialogDescription className="text-gray-400">
                Are you sure you want to permanently delete <strong className="text-white">{deleteTarget?.name || deleteTarget?.email}</strong>? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDeleteTarget(null)}
                className="border-white/10 text-white hover:bg-white/10"
                data-testid="cancel-delete-btn"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleDeleteUser}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-btn"
              >
                Delete User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
